import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Copy, Trash2 } from 'lucide-react';
import { BulkMembreData } from '@/types/bulk';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PosteSelector } from './PosteSelector';

interface Equipe {
  id: string;
  nom: string;
}

interface BulkMembreRowProps {
  index: number;
  data: BulkMembreData;
  equipes: Equipe[];
  onChange: (index: number, data: Partial<BulkMembreData>) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  canDelete: boolean;
  errors: Record<string, string>;
  isFirst?: boolean;
}

export function BulkMembreRow({
  index,
  data,
  equipes,
  onChange,
  onDuplicate,
  onDelete,
  canDelete,
  errors,
  isFirst,
}: BulkMembreRowProps) {
  return (
    <div className="grid grid-cols-[30px_1fr_1fr_1fr_140px_100px_80px_70px_auto] gap-2 items-start p-3 bg-card/50 rounded-lg border border-border/50">
      {/* Numéro de ligne */}
      <span className="text-sm text-muted-foreground font-medium pt-2 text-center">{index + 1}</span>

      {/* Prénom */}
      <div>
        <Input
          placeholder="Prénom *"
          value={data.prenom}
          onChange={(e) => onChange(index, { prenom: e.target.value })}
          className={errors.prenom ? 'border-red-500' : ''}
        />
        {errors.prenom && <span className="text-xs text-red-500">{errors.prenom}</span>}
      </div>

      {/* Nom */}
      <Input
        placeholder="Nom"
        value={data.nom}
        onChange={(e) => onChange(index, { nom: e.target.value })}
      />

      {/* Poste */}
      <PosteSelector
        value={data.poste}
        onChange={(poste) => onChange(index, { poste })}
      />

      {/* Spécialité — masqué pour simplifier l'UX */}
      {false && <Input
        placeholder="Spécialité"
        value={data.specialite}
        onChange={(e) => onChange(index, { specialite: e.target.value })}
      />}

      {/* Équipe */}
      <Select
        value={data.equipe_id || 'none'}
        onValueChange={(value) => onChange(index, { equipe_id: value === 'none' ? null : value })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Équipe" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Aucune équipe</SelectItem>
          {equipes.map((equipe) => (
            <SelectItem key={equipe.id} value={equipe.id}>
              {equipe.nom}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Taux net horaire */}
      <div>
        <div className="relative">
          <Input
            type="number"
            placeholder="Taux net"
            value={data.taux_horaire || ''}
            onChange={(e) => onChange(index, { taux_horaire: parseFloat(e.target.value) || 0 })}
            className={`pr-8 ${errors.taux_horaire ? 'border-red-500' : ''}`}
            min={0}
            step={0.5}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€/h</span>
        </div>
        {errors.taux_horaire && <span className="text-xs text-red-500">{errors.taux_horaire}</span>}
      </div>

      {/* Coefficient charges */}
      <div>
        <div className="relative">
          <Input
            type="number"
            placeholder="Coef"
            value={data.coefficient_charges || ''}
            onChange={(e) => onChange(index, { coefficient_charges: parseFloat(e.target.value) || 0 })}
            className={`pr-5 ${errors.coefficient_charges ? 'border-red-500' : ''}`}
            min={1}
            max={3}
            step={0.01}
          />
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">×</span>
        </div>
        {errors.coefficient_charges && <span className="text-xs text-red-500">{errors.coefficient_charges}</span>}
      </div>

      {/* Heures par jour */}
      <div>
        <Input
          type="number"
          placeholder="h/j"
          value={data.heures_par_jour || ''}
          onChange={(e) => onChange(index, { heures_par_jour: parseFloat(e.target.value) || 0 })}
          min={1}
          max={12}
          step={0.5}
        />
      </div>

      {/* Actions */}
      <div className="relative flex gap-1">
        {isFirst && (
          <span className="absolute -top-4 left-0 right-0 text-center text-[10px] text-white">Dupliquer</span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onDuplicate(index)}
          title="Dupliquer (copie poste, équipe, taux, charges)"
        >
          <Copy className="h-4 w-4" />
        </Button>
        {canDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(index)}
            title="Supprimer cette ligne"
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
